import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Users,
  QrCode,
  Plus,
  FileText,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmModal } from "@/components/ConfirmModal";
import { cn } from "@/lib/utils";
import { useGettingStarted, type TourTrack } from "./GettingStartedContext";

interface StepDef {
  id: TourTrack;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
}

const STEPS: StepDef[] = [
  {
    id: "partners",
    title: "Find new partners",
    description: "Browse contractors in your service areas.",
    icon: Users,
    badge: "Start here",
  },
  {
    id: "invite",
    title: "Invite trade partners",
    description: "Text an invite to contractors you know.",
    icon: QrCode,
  },
  {
    id: "referral",
    title: "Create a referral",
    description: "Learn where to start a new referral.",
    icon: Plus,
  },
  {
    id: "example",
    title: "View example referral",
    description: "Explore customer details, photos, and messages.",
    icon: FileText,
  },
];

export default function GettingStartedPanel() {
  const { dismissed, expanded, setExpanded, finish, startTrack } = useGettingStarted();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (dismissed) return null;

  // The panel button DOES NOT navigate. It starts the walkthrough track,
  // which dims the screen and highlights the real UI element the user must
  // tap. That real tap is what performs navigation (or, for the "example"
  // track, opens the modal directly).
  const handleStepClick = (step: StepDef) => {
    startTrack(step.id);
  };

  return (
    <>
      <Card className="shadow-md border border-gray-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-start justify-between px-5 pt-5 pb-3 text-left"
          aria-expanded={expanded}
          aria-controls="getting-started-body"
        >
          <div>
            <h3 className="font-semibold text-gray-900">Get started with Trade Loop</h3>
            <p className="text-sm text-gray-500 mt-1">
              Start by connecting with a trade partner.
            </p>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0 mt-1" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0 mt-1" />
          )}
        </button>

        {expanded && (
          <div id="getting-started-body" className="px-5 pb-5 space-y-2">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isFirst = index === 0;
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => handleStepClick(step)}
                  className={cn(
                    "w-full flex items-start gap-3 px-4 py-3 rounded-xl text-left transition-colors group",
                    isFirst
                      ? "bg-blue-50 hover:bg-blue-100"
                      : "bg-gray-50 hover:bg-gray-100"
                  )}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5 mt-0.5 flex-shrink-0",
                      isFirst ? "text-blue-700" : "text-gray-700"
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{step.title}</span>
                      {step.badge && (
                        <span className="text-xs font-medium text-blue-600">
                          {step.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{step.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
                </button>
              );
            })}

            {/* Edit Profile — same visual as the walkthrough steps, but plain
                navigation (no tour). Rendered as a Link so it behaves like a
                real anchor (right-click, cmd-click, etc.). */}
            <Link
              to="/edit-profile"
              className="w-full flex items-start gap-3 px-4 py-3 rounded-xl text-left transition-colors group bg-gray-50 hover:bg-gray-100"
            >
              <Pencil className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-700" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">Edit profile</span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Update your name, company, service areas, and photo.
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
            </Link>

            <div className="pt-3 border-t border-gray-200">
              <Button
                variant="outline"
                className="w-full mt-3"
                onClick={() => setConfirmOpen(true)}
              >
                Finish getting started
              </Button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Remove this guide from your dashboard whenever you're ready.
              </p>
            </div>
          </div>
        )}
      </Card>

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          finish();
          setConfirmOpen(false);
        }}
        title="Hide Getting Started?"
        message="This guide will no longer show on your dashboard. You won't be able to bring it back."
        confirmText="Hide it"
        cancelText="Keep showing"
      />
    </>
  );
}
